import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "@tanstack/react-form";
import { useMachine } from "@xstate/react";
import { Schema } from "effect";
import { useTranslation } from "react-i18next";
import { Button } from "@ascleo/ui/components/ui/button";
import { FieldError } from "@ascleo/ui/components/ui/field";
import { Input } from "@ascleo/ui/components/ui/input";
import { Label } from "@ascleo/ui/components/ui/label";
import {
  AlertCircle,
  Camera,
  CheckCircle2,
  KeyRound,
  Link2,
  Loader2,
} from "lucide-react";
import type { ChecklistPosition } from "../../lib/checklist/checklist-position";
import {
  isReady,
} from "../../lib/checklist/derive-preflight-checklist";
import { parseBrowserSetupPayload } from "../../lib/browser-setup-payload";
import {
  normalizeBridgeUrlInput,
  normalizeSolTokenInput,
} from "../../lib/connection-settings-normalization";
import { getFormErrorMessage } from "../../lib/forms/form-errors";
import type {
  BridgeSetupScannerHandle,
  StartBridgeSetupScannerOptions,
} from "../../platform/types";
import {
  isAcceptablePin,
  onboardingMachine,
  type OnboardingPayload,
} from "./machine";
import {
  CompactPracticeIdentity,
  DEFAULT_ACTIVE_BRANDING,
  FrontDeskAttribution,
  PracticeBrandScope,
  useOptionalBranding,
} from "../../branding";

function isSupportedBridgeAddress(value: string): boolean {
  const address = normalizeBridgeUrlInput(value);
  if (!URL.canParse(address)) return false;
  const protocol = new URL(address).protocol;
  return protocol === "https:" || protocol === "http:";
}

const ManualSetupFormSchema = Schema.Struct({
  insuiteRestUrl: Schema.String.pipe(
    Schema.check(Schema.makeFilter((value) => normalizeBridgeUrlInput(value).length > 0, {
      message: "validation.required",
    })),
    Schema.check(Schema.makeFilter(isSupportedBridgeAddress, {
      message: "validation.invalid-url",
    })),
  ),
  solToken: Schema.String.pipe(
    Schema.check(Schema.makeFilter((value) => normalizeSolTokenInput(value) !== undefined, {
      message: "validation.required",
    })),
  ),
});

type ManualSetupFormValues = typeof ManualSetupFormSchema.Type;

const TokenEntryFormSchema = Schema.Struct({
  solToken: Schema.String.pipe(
    Schema.check(Schema.makeFilter((value) => normalizeSolTokenInput(value) !== undefined, {
      message: "validation.required",
    })),
  ),
});

type TokenEntryFormValues = typeof TokenEntryFormSchema.Type;

const TOKEN_ENTRY_DEFAULT_VALUES: TokenEntryFormValues = { solToken: "" };

const MANUAL_SETUP_DEFAULT_VALUES: ManualSetupFormValues = {
  insuiteRestUrl: "",
  solToken: "",
};

/**
 * A card reader / terminal choice offered on the preflight screen.
 *
 * @since 0.0.0
 */
export interface OnboardingTerminal {
  readonly id: string;
  readonly name: string;
}

/**
 * Result of a completed device checklist fetch: the derived positions plus the
 * practice name confirmed from inSuite.
 *
 * @since 0.0.0
 */
export interface OnboardingChecklistResult {
  readonly positions: readonly ChecklistPosition[];
  readonly practiceName: string | undefined;
}

/**
 * Result handed to the shell when onboarding completes.
 *
 * @since 0.0.0
 */
export interface OnboardingResult {
  readonly payload: OnboardingPayload;
  readonly practiceName: string | undefined;
  readonly deviceName: string;
  readonly cardReaderId: string | undefined;
  readonly pin: string;
}

/**
 * Injected dependencies for {@link OnboardingFlow}. Keeping I/O out of the
 * component makes it shell-agnostic (Electron/iPad/Web) and testable.
 *
 * @since 0.0.0
 */
export interface OnboardingFlowProps {
  /** Platform QR scanner; absent on Electron → manual connection entry. */
  readonly startBridgeSetupScanner?: (
    options: StartBridgeSetupScannerOptions,
  ) => Promise<BridgeSetupScannerHandle>;
  /** Fetch + derive the preflight checklist for the scanned payload. */
  readonly fetchChecklist: (
    payload: OnboardingPayload,
    cardReaderId: string | undefined,
  ) => Promise<OnboardingChecklistResult>;
  /** Available card readers/terminals. */
  readonly terminals?: readonly OnboardingTerminal[];
  /**
   * Called once when the ceremony completes; the shell persists the result and starts patient
   * mode. A rejection returns the wizard to the setup step with an error instead of leaving it
   * on the success screen.
   */
  readonly onComplete: (result: OnboardingResult) => Promise<void> | void;
}

/**
 * The on-device onboarding ("pairing ceremony"): Step-0 preparation → welcome →
 * scan → auto preflight → configure → success. The QR scan is primary, with a
 * manual Bridge address and SOLTOKEN fallback; the default PIN is rejected.
 *
 * @since 0.0.0
 * @example
 * ```tsx
 * <OnboardingFlow fetchChecklist={fetchChecklist} onComplete={startKiosk} />
 * ```
 */
export function OnboardingFlow({
  startBridgeSetupScanner,
  fetchChecklist,
  terminals = [],
  onComplete,
}: OnboardingFlowProps): React.ReactElement {
  const { t } = useTranslation();
  const [snapshot, send] = useMachine(onboardingMachine);
  const state = snapshot.value;
  const openWelcome = useCallback(() => send({ type: "NEXT" }), [send]);
  const openScanner = useCallback(() => send({ type: "SCAN" }), [send]);
  const openPreparation = useCallback(
    () => send({ type: "OPEN_PREPARATION" }),
    [send],
  );
  const applyScannedPayload = useCallback(
    (payload: OnboardingPayload) => send({ type: "SCANNED", payload }),
    [send],
  );
  const applyManualPayload = useCallback(
    (payload: OnboardingPayload) => send({ type: "MANUAL_ENTRY", payload }),
    [send],
  );
  const showScanError = useCallback(
    (message?: string) => send({ type: "SCAN_ERROR", message }),
    [send],
  );
  const showCameraDenied = useCallback(
    () => send({ type: "CAMERA_DENIED" }),
    [send],
  );
  const retryScanner = useCallback(() => send({ type: "RETRY" }), [send]);
  const enterToken = useCallback(
    (solToken: string) => send({ type: "TOKEN_ENTERED", solToken }),
    [send],
  );
  const rescan = useCallback(() => send({ type: "RESCAN" }), [send]);
  const editToken = useCallback(() => send({ type: "EDIT_TOKEN" }), [send]);
  const reportChecklistFailure = useCallback(
    () => send({ type: "CHECKLIST_FAILED" }),
    [send],
  );
  const updateChecklist = useCallback(
    (result: OnboardingChecklistResult) =>
      send({
        type: "CHECKLIST_UPDATED",
        positions: result.positions,
        practiceName: result.practiceName,
      }),
    [send],
  );
  const updateDeviceName = useCallback(
    (deviceName: string) => send({ type: "SET_DEVICE_NAME", deviceName }),
    [send],
  );
  const updatePracticeName = useCallback(
    (practiceName: string) =>
      send({ type: "SET_PRACTICE_NAME", practiceName }),
    [send],
  );
  const updateCardReader = useCallback(
    (cardReaderId: string) => send({ type: "SET_CARD_READER", cardReaderId }),
    [send],
  );
  const updatePin = useCallback(
    (pin: string) => send({ type: "SET_PIN", pin }),
    [send],
  );
  const persistenceInFlight = useRef(false);
  const finishOnboarding = useCallback(async () => {
    if (persistenceInFlight.current || !snapshot.can({ type: "FINISH" })) return;
    const { payload, practiceName, deviceName, cardReaderId, pin } =
      snapshot.context;
    if (!payload) return;
    persistenceInFlight.current = true;
    send({ type: "FINISH" });
    try {
      await onComplete({ payload, practiceName, deviceName, cardReaderId, pin });
      send({ type: "PERSIST_SUCCEEDED" });
    } catch {
      send({
        type: "PERSIST_FAILED",
        message: "settings.save-error",
      });
    } finally {
      persistenceInFlight.current = false;
    }
  }, [onComplete, send, snapshot]);

  switch (state) {
    case "preparation":
      return <PrepareScreen onNext={openWelcome} />;
    case "welcome":
      return <WelcomeScreen onScan={openScanner} onPrepare={openPreparation} />;
    case "scanning":
      return (
        <ScanScreen
          startBridgeSetupScanner={startBridgeSetupScanner}
          onScanned={applyScannedPayload}
          onManualEntry={applyManualPayload}
          onScanError={showScanError}
          onCameraDenied={showCameraDenied}
        />
      );
    case "scanError":
      return (
        <ErrorScreen
          title={t("onboarding.scan.error-title")}
          hint={snapshot.context.error}
          onRetry={retryScanner}
          onManualEntry={applyManualPayload}
        />
      );
    case "cameraDenied":
      return (
        <ErrorScreen
          title={t("onboarding.scan.camera-denied-title")}
          hint={t("onboarding.scan.camera-denied-hint")}
          onRetry={retryScanner}
          onManualEntry={applyManualPayload}
        />
      );
    case "tokenEntry":
      return <TokenEntryScreen onSubmit={enterToken} onRescan={rescan} />;
    case "preflight":
      return (
        <PreflightScreen
          payload={snapshot.context.payload}
          persistError={snapshot.context.error}
          checklistError={snapshot.context.checklistError}
          onChecklistFailed={reportChecklistFailure}
          onEditToken={editToken}
          onRescan={rescan}
          checklist={snapshot.context.checklist}
          practiceName={snapshot.context.practiceName}
          deviceName={snapshot.context.deviceName}
          cardReaderId={snapshot.context.cardReaderId}
          pin={snapshot.context.pin}
          terminals={terminals}
          fetchChecklist={fetchChecklist}
          onChecklist={updateChecklist}
          onPracticeName={updatePracticeName}
          onDeviceName={updateDeviceName}
          onCardReader={updateCardReader}
          onPin={updatePin}
          onFinish={finishOnboarding}
        />
      );
    case "persisting":
      return (
        <Shell practiceName={snapshot.context.practiceName}>
          <output data-testid="frontdesk-onboarding-persisting" aria-busy="true" className="flex items-center gap-3">
            <Loader2 className="size-6 animate-spin" aria-hidden="true" />
            <span>{t("onboarding.persisting")}</span>
          </output>
        </Shell>
      );
    case "success":
      return <SuccessScreen practiceName={snapshot.context.practiceName} />;
    default:
      return <div data-testid="frontdesk-onboarding-unknown" />;
  }
}

function Shell({
  children,
  practiceName,
}: {
  children: React.ReactNode;
  practiceName?: string;
}): React.ReactElement {
  const branding = useOptionalBranding();
  const activeBranding = branding?.active ?? DEFAULT_ACTIVE_BRANDING;

  return (
    <div className="mx-auto flex min-h-full w-full max-w-[1280px] flex-col gap-8 px-10 py-8">
      <PracticeBrandScope
        active={activeBranding}
        className="flex w-fit max-w-64 flex-col gap-0.5 rounded-2xl bg-primary-subtle px-3 py-2"
      >
        <CompactPracticeIdentity
          active={activeBranding}
          practiceName={practiceName}
        />
        <FrontDeskAttribution className="ps-0.5" />
      </PracticeBrandScope>
      {children}
    </div>
  );
}

function PrepareScreen({ onNext }: { onNext: () => void }): React.ReactElement {
  const { t } = useTranslation();
  const steps = [
    t("onboarding.prepare.step1"),
    t("onboarding.prepare.step2"),
    t("onboarding.prepare.step3"),
  ];
  return (
    <Shell>
      <h1 className="font-heading text-[34px] font-bold">
        {t("onboarding.prepare.title")}
      </h1>
      <p className="text-lg text-muted-foreground">
        {t("onboarding.prepare.subtitle")}
      </p>
      <ol className="flex flex-col gap-4">
        {steps.map((step, index) => (
          <li
            key={step}
            className="flex items-start gap-4 rounded-3xl bg-primary-subtle p-6"
          >
            <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
              {index + 1}
            </span>
            <span className="text-lg">{step}</span>
          </li>
        ))}
      </ol>
      <Button
        data-testid="frontdesk-onboarding-prepare-next"
        size="2xl"
        onClick={onNext}
      >
        {t("onboarding.prepare.cta")}
      </Button>
    </Shell>
  );
}

function WelcomeScreen({
  onScan,
  onPrepare,
}: {
  onScan: () => void;
  onPrepare: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <Shell>
      <h1 className="font-heading text-[34px] font-bold">
        {t("onboarding.welcome.title")}
      </h1>
      <button
        type="button"
        data-testid="frontdesk-onboarding-scan-btn"
        onClick={onScan}
        className="flex flex-col items-center gap-4 rounded-3xl bg-primary-subtle p-16 text-primary-ink transition hover:bg-primary-soft"
      >
        <Camera className="size-16" />
        <span className="font-heading text-2xl font-semibold">
          {t("onboarding.welcome.scan")}
        </span>
      </button>
      <Button
        variant="ghost"
        data-testid="frontdesk-onboarding-prepare-link"
        onClick={onPrepare}
      >
        {t("onboarding.welcome.prepare-link")}
      </Button>
    </Shell>
  );
}

function ScanScreen({
  startBridgeSetupScanner,
  onScanned,
  onManualEntry,
  onScanError,
  onCameraDenied,
}: {
  startBridgeSetupScanner?: (
    options: StartBridgeSetupScannerOptions,
  ) => Promise<BridgeSetupScannerHandle>;
  onScanned: (payload: OnboardingPayload) => void;
  onManualEntry: (payload: OnboardingPayload) => void;
  onScanError: (message?: string) => void;
  onCameraDenied: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(
    function runBridgeSetupScanner() {
      const video = videoRef.current;
      if (!startBridgeSetupScanner || !video) return;
      let handle: BridgeSetupScannerHandle | undefined;
      let cancelled = false;
      void startBridgeSetupScanner({
        video,
        onDecode: (rawValue: string) => {
          const parsed = parseBrowserSetupPayload(rawValue);
          if (parsed.ok) {
            onScanned({
              insuiteRestUrl: parsed.value.insuiteRestUrl,
              solToken: parsed.value.solToken,
            });
          } else {
            onScanError(parsed.error);
          }
        },
        onError: () => onCameraDenied(),
      })
        .then((h) => {
          if (cancelled) h.stop();
          else handle = h;
          return h;
        })
        .catch(() => onCameraDenied());
      return function stopBridgeSetupScanner() {
        cancelled = true;
        handle?.stop();
      };
    },
    [startBridgeSetupScanner, onScanned, onScanError, onCameraDenied],
  );

  return (
    <Shell>
      {startBridgeSetupScanner ? (
        <>
          <p className="text-lg">{t("onboarding.scan.scanning")}</p>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video
            ref={videoRef}
            data-testid="frontdesk-onboarding-scan-video"
            aria-label={t("onboarding.scan.scanning")}
            className="aspect-video w-full rounded-3xl bg-black"
            playsInline
            muted
          />
        </>
      ) : null}
      <ManualSetupForm onSubmit={onManualEntry} />
    </Shell>
  );
}

function ManualSetupForm({
  onSubmit,
}: {
  onSubmit: (payload: OnboardingPayload) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const form = useForm({
    defaultValues: MANUAL_SETUP_DEFAULT_VALUES,
    validators: {
      onChange: Schema.toStandardSchemaV1(ManualSetupFormSchema),
    },
    onSubmit: ({ value }) => {
      const solToken = normalizeSolTokenInput(value.solToken);
      if (solToken === undefined) return;
      onSubmit({
        insuiteRestUrl: normalizeBridgeUrlInput(value.insuiteRestUrl),
        solToken,
      });
    },
  });

  return (
    <form
      data-testid="frontdesk-onboarding-manual-form"
      className="flex flex-col gap-5 rounded-3xl border border-border bg-card p-6 shadow-sm"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        void form.handleSubmit();
      }}
    >
      <div className="flex items-start gap-4">
        <span className="flex size-11 shrink-0 items-center justify-center rounded-2xl bg-primary-subtle text-primary-ink">
          <Link2 className="size-5" aria-hidden="true" />
        </span>
        <div className="space-y-1">
          <h2 className="font-heading text-xl font-semibold">
            {t("onboarding.scan.manual-title")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t("onboarding.scan.manual-description")}
          </p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <form.Field name="insuiteRestUrl">
          {(field) => {
            const error = field.state.meta.errors[0];
            const errorMessage =
              error === undefined ? undefined : t(getFormErrorMessage(error));
            return (
              <div className="flex flex-col gap-2">
                <Label htmlFor="frontdesk-onboarding-manual-address">
                  {t("onboarding.scan.address-label")}
                </Label>
                <Input
                  id="frontdesk-onboarding-manual-address"
                  data-testid="frontdesk-onboarding-manual-address-input"
                  size="xl"
                  inputMode="url"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder={t("onboarding.scan.address-placeholder")}
                  value={field.state.value}
                  aria-invalid={errorMessage !== undefined}
                  aria-describedby={
                    errorMessage === undefined
                      ? undefined
                      : "frontdesk-onboarding-manual-address-error"
                  }
                  onChange={(event) => field.handleChange(event.target.value)}
                  onBlur={() => {
                    field.handleChange(
                      normalizeBridgeUrlInput(field.state.value),
                    );
                    field.handleBlur();
                  }}
                />
                {errorMessage === undefined ? null : (
                  <FieldError
                    id="frontdesk-onboarding-manual-address-error"
                    data-testid="frontdesk-onboarding-manual-address-error"
                  >
                    {errorMessage}
                  </FieldError>
                )}
              </div>
            );
          }}
        </form.Field>

        <form.Field name="solToken">
          {(field) => {
            const error = field.state.meta.errors[0];
            const errorMessage =
              error === undefined ? undefined : t(getFormErrorMessage(error));
            return (
              <div className="flex flex-col gap-2">
                <Label htmlFor="frontdesk-onboarding-manual-soltoken">
                  {t("onboarding.scan.soltoken-label")}
                </Label>
                <div className="relative">
                  <KeyRound
                    className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                    aria-hidden="true"
                  />
                  <Input
                    id="frontdesk-onboarding-manual-soltoken"
                    data-testid="frontdesk-onboarding-manual-soltoken-input"
                    className="pl-11 font-mono"
                    size="xl"
                    type="password"
                    autoComplete="off"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    placeholder={t("onboarding.scan.soltoken-placeholder")}
                    value={field.state.value}
                    aria-invalid={errorMessage !== undefined}
                    aria-describedby={
                      errorMessage === undefined
                        ? undefined
                        : "frontdesk-onboarding-manual-soltoken-error"
                    }
                    onChange={(event) => field.handleChange(event.target.value)}
                    onBlur={() => {
                      field.handleChange(
                        normalizeSolTokenInput(field.state.value) ?? "",
                      );
                      field.handleBlur();
                    }}
                  />
                </div>
                {errorMessage === undefined ? null : (
                  <FieldError
                    id="frontdesk-onboarding-manual-soltoken-error"
                    data-testid="frontdesk-onboarding-manual-soltoken-error"
                  >
                    {errorMessage}
                  </FieldError>
                )}
              </div>
            );
          }}
        </form.Field>
      </div>

      <form.Subscribe
        selector={(state) =>
          state.canSubmit &&
          normalizeBridgeUrlInput(state.values.insuiteRestUrl).length > 0 &&
          normalizeSolTokenInput(state.values.solToken) !== undefined
        }
      >
        {(canSubmit) => (
          <Button
            type="submit"
            data-testid="frontdesk-onboarding-manual-submit"
            size="xl"
            disabled={!canSubmit}
          >
            {t("onboarding.scan.manual-submit")}
          </Button>
        )}
      </form.Subscribe>
    </form>
  );
}

function TokenEntryScreen({
  onSubmit,
  onRescan,
}: {
  onSubmit: (solToken: string) => void;
  onRescan: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const form = useForm({
    defaultValues: TOKEN_ENTRY_DEFAULT_VALUES,
    validators: {
      onChange: Schema.toStandardSchemaV1(TokenEntryFormSchema),
    },
    onSubmit: ({ value }) => {
      const solToken = normalizeSolTokenInput(value.solToken);
      if (solToken !== undefined) onSubmit(solToken);
    },
  });

  return (
    <Shell>
      <h1 className="font-heading text-[34px] font-bold">
        {t("onboarding.token.title")}
      </h1>
      <p
        id="frontdesk-onboarding-token-description"
        className="text-lg text-muted-foreground"
      >
        {t("onboarding.token.description")}
      </p>
      <form
        data-testid="frontdesk-onboarding-token-form"
        className="flex flex-col gap-5"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void form.handleSubmit();
        }}
      >
        <form.Field name="solToken">
          {(field) => {
            const error = field.state.meta.errors[0];
            const errorMessage =
              error === undefined ? undefined : t(getFormErrorMessage(error));
            return (
              <div className="flex flex-col gap-2">
                <Label htmlFor="frontdesk-onboarding-token-input">
                  {t("onboarding.scan.soltoken-label")}
                </Label>
                <Input
                  id="frontdesk-onboarding-token-input"
                  data-testid="frontdesk-onboarding-token-input"
                  className="font-mono"
                  size="2xl"
                  type="password"
                  autoComplete="off"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder={t("onboarding.scan.soltoken-placeholder")}
                  value={field.state.value}
                  aria-invalid={errorMessage !== undefined}
                  aria-describedby={
                    errorMessage === undefined
                      ? "frontdesk-onboarding-token-description"
                      : "frontdesk-onboarding-token-description frontdesk-onboarding-token-error"
                  }
                  onChange={(event) => field.handleChange(event.target.value)}
                  onBlur={field.handleBlur}
                />
                {errorMessage === undefined ? null : (
                  <FieldError
                    id="frontdesk-onboarding-token-error"
                    data-testid="frontdesk-onboarding-token-error"
                  >
                    {errorMessage}
                  </FieldError>
                )}
              </div>
            );
          }}
        </form.Field>
        <form.Subscribe
          selector={(state) =>
            normalizeSolTokenInput(state.values.solToken) !== undefined
          }
        >
          {(canSubmit) => (
            <Button
              type="submit"
              data-testid="frontdesk-onboarding-token-submit"
              size="2xl"
              disabled={!canSubmit}
            >
              {t("onboarding.token.submit")}
            </Button>
          )}
        </form.Subscribe>
      </form>
      <Button
        variant="ghost"
        size="2xl"
        data-testid="frontdesk-onboarding-token-rescan"
        onClick={onRescan}
      >
        {t("onboarding.token.rescan")}
      </Button>
    </Shell>
  );
}

function ChecklistFailure({
  onRetry,
  onEditToken,
  onRescan,
}: {
  onRetry: () => void;
  onEditToken: () => void;
  onRescan: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      data-testid="frontdesk-onboarding-checklist-error"
      className="flex flex-col gap-4 rounded-2xl bg-destructive-subtle p-5 text-destructive-ink"
    >
      <div className="flex items-center gap-3">
        <AlertCircle className="size-6 shrink-0" aria-hidden="true" />
        <p className="m-0 text-lg">{t("onboarding.preflight.checklist-error")}</p>
      </div>
      <div className="flex flex-wrap gap-3">
        <Button
          size="2xl"
          data-testid="frontdesk-onboarding-checklist-retry"
          onClick={onRetry}
        >
          {t("onboarding.preflight.checklist-retry")}
        </Button>
        <Button
          size="2xl"
          variant="outline"
          data-testid="frontdesk-onboarding-edit-token"
          onClick={onEditToken}
        >
          {t("onboarding.preflight.edit-token")}
        </Button>
        <Button
          size="2xl"
          variant="ghost"
          data-testid="frontdesk-onboarding-rescan"
          onClick={onRescan}
        >
          {t("onboarding.preflight.rescan")}
        </Button>
      </div>
    </div>
  );
}

function ErrorScreen({
  title,
  hint,
  onRetry,
  onManualEntry,
}: {
  title: string;
  hint?: string;
  onRetry: () => void;
  onManualEntry: (payload: OnboardingPayload) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <Shell>
      <div className="flex flex-col items-center gap-4 rounded-3xl bg-destructive/10 p-16 text-center">
        <AlertCircle className="size-16 text-destructive" />
        <h1 className="font-heading text-2xl font-semibold">{title}</h1>
        {hint ? <p className="text-muted-foreground">{hint}</p> : null}
      </div>
      <Button
        data-testid="frontdesk-onboarding-retry-btn"
        size="2xl"
        onClick={onRetry}
      >
        {t("onboarding.scan.retry")}
      </Button>
      <ManualSetupForm onSubmit={onManualEntry} />
    </Shell>
  );
}

function SetupBlockers({
  checklist,
}: {
  checklist: readonly ChecklistPosition[];
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <>
      {checklist.filter((position) => position.state === "failed" && position.severity === "blocker").map((position) => (
        <FieldError key={position.code} data-testid={`frontdesk-onboarding-blocker-${position.code}`}>
          {t(position.fix)}
        </FieldError>
      ))}
    </>
  );
}

function PreflightScreen(props: {
  payload: OnboardingPayload | null;
  /** Set when persisting a completed setup failed; shown above the checklist. */
  persistError?: string;
  /** Set when the Bridge refused or could not answer the checklist request. */
  checklistError?: string;
  onChecklistFailed: () => void;
  onEditToken: () => void;
  onRescan: () => void;
  checklist: readonly ChecklistPosition[];
  practiceName: string | undefined;
  deviceName: string;
  cardReaderId: string | undefined;
  pin: string;
  terminals: readonly OnboardingTerminal[];
  fetchChecklist: (
    payload: OnboardingPayload,
    cardReaderId: string | undefined,
  ) => Promise<OnboardingChecklistResult>;
  onChecklist: (result: OnboardingChecklistResult) => void;
  onPracticeName: (value: string) => void;
  onDeviceName: (value: string) => void;
  onCardReader: (value: string) => void;
  onPin: (value: string) => void;
  onFinish: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const {
    payload,
    persistError,
    checklist,
    practiceName,
    deviceName,
    cardReaderId,
    pin,
    terminals,
    fetchChecklist,
    onChecklist,
    checklistError,
  } = props;
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);
  const retryChecklist = useCallback(() => setAttempt((value) => value + 1), []);

  // Hold the latest callbacks in refs so the load effect fires only when the
  // scanned payload / card reader changes — not on every render (which would
  // otherwise refetch in a loop and never let the form settle).
  const fetchChecklistRef = useRef(fetchChecklist);
  fetchChecklistRef.current = fetchChecklist;
  const onChecklistRef = useRef(onChecklist);
  onChecklistRef.current = onChecklist;
  const onChecklistFailedRef = useRef(props.onChecklistFailed);
  onChecklistFailedRef.current = props.onChecklistFailed;

  useEffect(
    function loadPreflightChecklist() {
      if (!payload) return;
      let cancelled = false;
      setLoading(true);
      void fetchChecklistRef
        .current(payload, cardReaderId)
        .then((result) => {
          if (!cancelled) {
            onChecklistRef.current(result);
            setLoading(false);
          }
          return result;
        })
        .catch(() => {
          // A refused token or unreachable Bridge must be visible: an empty checklist alone
          // leaves the setup unfinishable with no way forward.
          if (!cancelled) {
            onChecklistFailedRef.current();
            setLoading(false);
          }
        });
      return function cancelLoad() {
        cancelled = true;
      };
    },
    [payload, cardReaderId, attempt],
  );

  const pinError = useMemo(
    () =>
      pin.length > 0 && !isAcceptablePin(pin)
        ? t("validation.pin-default-rejected")
        : undefined,
    [pin, t],
  );
  const canFinish =
    isAcceptablePin(pin) && checklist.length > 0 && isReady(checklist);

  return (
    <Shell practiceName={practiceName}>
      {persistError === undefined ? null : (
        <div
          role="alert"
          data-testid="frontdesk-onboarding-persist-error"
          className="flex items-center gap-3 rounded-2xl bg-destructive-subtle px-4 py-3 text-destructive-ink"
        >
          <AlertCircle className="size-5 shrink-0" />
          <p className="m-0 text-sm">{t(persistError)}</p>
        </div>
      )}
      <h1 className="font-heading text-[34px] font-bold">
        {t("onboarding.welcome.title")}
      </h1>
      {checklistError === undefined ? null : (
        <ChecklistFailure
          onRetry={retryChecklist}
          onEditToken={props.onEditToken}
          onRescan={props.onRescan}
        />
      )}
      <SetupBlockers checklist={checklist} />

      <div className="flex flex-col gap-2">
        <Label htmlFor="frontdesk-onboarding-practice-name">
          {t("onboarding.preflight.practice")}
        </Label>
        <Input
          id="frontdesk-onboarding-practice-name"
          data-testid="frontdesk-onboarding-practice-name-input"
          placeholder={t("settings.practice.name-placeholder")}
          value={practiceName ?? ""}
          onChange={(event) => props.onPracticeName(event.target.value)}
        />
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="frontdesk-onboarding-device-name">
          {t("onboarding.preflight.device-name")}
        </Label>
        <Input
          id="frontdesk-onboarding-device-name"
          data-testid="frontdesk-onboarding-device-name-input"
          placeholder={t("onboarding.preflight.device-name-placeholder")}
          value={deviceName}
          onChange={(event) => props.onDeviceName(event.target.value)}
        />
      </div>

      {terminals.length > 0 ? (
        <div className="flex flex-col gap-2">
          <Label>{t("onboarding.preflight.card-reader")}</Label>
          <div className="flex flex-wrap gap-2">
            {terminals.map((terminal) => (
              <Button
                key={terminal.id}
                type="button"
                data-testid={`frontdesk-onboarding-terminal-${terminal.id}`}
                variant={cardReaderId === terminal.id ? "default" : "outline"}
                onClick={() => props.onCardReader(terminal.id)}
              >
                {terminal.name}
              </Button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        <Label htmlFor="frontdesk-onboarding-pin">
          {t("onboarding.preflight.pin")}
        </Label>
        <Input
          id="frontdesk-onboarding-pin"
          data-testid="frontdesk-onboarding-pin-input"
          type="password"
          inputMode="numeric"
          maxLength={8}
          placeholder={t("onboarding.preflight.pin-placeholder")}
          value={pin}
          onChange={(event) => props.onPin(event.target.value)}
        />
        {pinError ? (
          <FieldError data-testid="frontdesk-onboarding-pin-error">
            {pinError}
          </FieldError>
        ) : null}
      </div>

      <Button
        data-testid="frontdesk-onboarding-finish-btn"
        size="2xl"
        disabled={loading || !canFinish}
        aria-busy={loading}
        onClick={props.onFinish}
      >
        {t("onboarding.preflight.finish")}
      </Button>
    </Shell>
  );
}

function SuccessScreen({
  practiceName,
}: {
  practiceName?: string;
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <Shell practiceName={practiceName}>
      <div data-testid="frontdesk-onboarding-success" className="flex flex-col items-center gap-4 rounded-3xl bg-success p-16 text-center text-white">
        <CheckCircle2 className="size-16" />
        <h1 className="font-heading text-2xl font-semibold">
          {t("onboarding.success.title")}
        </h1>
        <output>{t("onboarding.success.detail")}</output>
        <p className="text-sm opacity-80">{t("onboarding.success.starting")}</p>
      </div>
    </Shell>
  );
}
